import type { SidebarPayload, Bucket, BucketKind } from "@/types";
import type { SyncList, SyncTask } from "@/stores/taskStore";
import { byCompletedAtDesc, byPosition, dateInZone } from "@/lib/views/order";
import { belongsToScope, groupPlanned, listScopeKey, sortFor, type PlannedGroupKey } from "@/lib/views/scopes";
import { childrenCounts, toTaskData, type TaskJson } from "@/lib/views/taskData";

export * from "@/lib/views/order";
export * from "@/lib/views/scopes";
export * from "@/lib/views/taskData";

// Everything a view reads: the local store's rows plus the user's today and zone.
// Visibility by time of day is not a view's concern — pages gate rows by the device clock.
export interface ViewSource {
  tasks: SyncTask[];
  lists: SyncList[];
  today: string;
  zone: string;
}

interface Prepared {
  rows: TaskJson[];
  byId: Map<string, TaskJson>;
  lists: SyncList[];
  today: string;
  zone: string;
}

// Pages and the sidebar reading one source object share a single preparation.
const preparedBySource = new WeakMap<ViewSource, Prepared>();

function prepare(source: ViewSource): Prepared {
  let prepared = preparedBySource.get(source);
  if (!prepared) {
    prepared = prepareRows(source);
    preparedBySource.set(source, prepared);
  }
  return prepared;
}

function prepareRows({ tasks, lists, today, zone }: ViewSource): Prepared {
  const kept = tasks.filter((t) => !t.discardedAt);
  const keptLists = lists.filter((l) => !l.discardedAt);
  const ctx = { today, listsById: new Map(keptLists.map((l) => [l.id, l])), childrenCounts: childrenCounts(kept) };
  const rows = kept.map((t) => toTaskData(t, ctx));
  return { rows, byId: new Map(rows.map((r) => [r.id, r])), lists: keptLists, today, zone };
}

const isPending = (task: TaskJson) => task.status === "pending";

// TemplatesController#show: every template root, open or finished, by position.
export function templates(source: ViewSource): TaskJson[] {
  const { rows, today } = prepare(source);
  return rows.filter((t) => belongsToScope(t, "templates", today)).sort(byPosition);
}

function listRowsOf({ rows, today }: Prepared, listId: string): { pending: TaskJson[]; completed: TaskJson[] } {
  const inList = rows.filter((t) => belongsToScope(t, listScopeKey(listId), today));
  return {
    pending: inList.filter(isPending).sort(byPosition),
    completed: inList.filter((t) => !isPending(t)).sort(byCompletedAtDesc),
  };
}

// ListsController#show: List#pending_rows and List#rows.completed.
export function listRows(source: ViewSource, listId: string): { pending: TaskJson[]; completed: TaskJson[] } {
  return listRowsOf(prepare(source), listId);
}

// TasksController#show's subtasks for each parent: every kept child, open or finished, by position.
export function subtasks(source: ViewSource, parentIds: readonly string[]): Record<string, TaskJson[]> {
  const wanted = new Set(parentIds);
  const out: Record<string, TaskJson[]> = Object.fromEntries(parentIds.map((id) => [id, []]));
  if (wanted.size === 0) return out;
  for (const row of prepare(source).rows) {
    if (row.parentId !== null && wanted.has(row.parentId)) out[row.parentId].push(row);
  }
  for (const children of Object.values(out)) children.sort(byPosition);
  return out;
}

// The local copy's rows among `ids`: the kept ones by id, and the ids an unsent op discards.
export function tasksAmong(source: ViewSource, ids: readonly string[]): { rows: Record<string, TaskJson>; discarded: string[] } {
  const { byId } = prepare(source);
  const wanted = new Set(ids);
  const rows: Record<string, TaskJson> = {};
  for (const id of ids) {
    const row = byId.get(id);
    if (row) rows[id] = row;
  }
  const discarded = source.tasks.filter((t) => t.discardedAt && wanted.has(t.id)).map((t) => t.id);
  return { rows, discarded };
}

// PlannedController#show over Task.planned_for.
export function planned(source: ViewSource): { key: PlannedGroupKey; tasks: TaskJson[] }[] {
  const { rows, today } = prepare(source);
  const dated = rows.filter((t) => isPending(t) && belongsToScope(t, "planned", today)).sort(sortFor("planned", today));
  return groupPlanned(dated, today);
}

function boardOf({ rows, today }: Prepared): TaskJson[] {
  return rows.filter((t) => isPending(t) && belongsToScope(t, "myday", today)).sort(sortFor("myday", today));
}

// Mirrors Task.completed_on: completed today in the user's zone, listed at its highest
// completed level.
function completedOnToday({ rows, byId, today, zone }: Prepared): TaskJson[] {
  return rows
    .filter((t) => {
      if (isPending(t) || t.reusable || !t.completedAt) return false;
      if (dateInZone(t.completedAt, zone) !== today) return false;
      return t.parentId === null || byId.get(t.parentId)?.status !== "completed";
    })
    .sort(byCompletedAtDesc);
}

// Mirrors Task.backlog_for: an open task on today's board by neither route.
function backlogNonEmpty({ rows, today }: Prepared): boolean {
  return rows.some((t) => isPending(t) && !t.reusable && t.myDayOn !== today && t.dueOn !== today);
}

// MyDayController#show.
export function myDay(source: ViewSource): { pending: TaskJson[]; completed: TaskJson[]; backlogNonEmpty: boolean } {
  const prepared = prepare(source);
  return { pending: boardOf(prepared), completed: completedOnToday(prepared), backlogNonEmpty: backlogNonEmpty(prepared) };
}

export type ZenFrameRequest =
  | { kind: "myDay" }
  | { kind: "inOrder" }
  | { kind: "list"; listId: string }
  | { kind: "task"; taskId: string };

export interface ZenFrame {
  kind: "myDay" | "list" | "task";
  label: string;
  contextId: string | null;
  tasks: TaskJson[];
}

const INBOX_LIST_ALIAS = "inbox";

// ZenController#resolve_frame; null where the server redirects away.
export function zenFrame(source: ViewSource, request: ZenFrameRequest): ZenFrame | null {
  const prepared = prepare(source);
  switch (request.kind) {
    case "myDay":
      return { kind: "myDay", label: "Мой день", contextId: null, tasks: boardOf(prepared) };
    case "inOrder":
      return {
        kind: "myDay",
        label: "Мой день › По порядку",
        contextId: "in-order",
        tasks: boardOf(prepared).filter((t) => t.myDaySequential),
      };
    case "list": {
      const list = prepared.lists.find((l) =>
        request.listId === INBOX_LIST_ALIAS ? l.kind === "inbox" : l.id === request.listId,
      );
      if (!list) return null;
      return { kind: "list", label: list.name, contextId: list.id, tasks: listRowsOf(prepared, list.id).pending };
    }
    case "task": {
      const task = prepared.byId.get(request.taskId);
      if (!task || task.reusable) return null;
      const children = prepared.rows.filter((t) => t.parentId === task.id && isPending(t)).sort(byPosition);
      return { kind: "task", label: task.title, contextId: task.id, tasks: children };
    }
  }
}

function bucket(prepared: Prepared, kind: BucketKind): Bucket | null {
  const list = prepared.lists.find((l) => l.kind === kind);
  if (!list) return null;
  const count = listRowsOf(prepared, list.id).pending.length;
  return { id: list.id, name: list.name, kind: list.kind, count };
}

// SidebarSerializer; countDots is a user setting the store doesn't hold.
export function sidebarCounts(source: ViewSource, countDots: boolean): SidebarPayload {
  const prepared = prepare(source);
  const { rows, today } = prepared;
  const openWork = rows.filter((t) => isPending(t) && !t.reusable);
  return {
    smartLists: [
      { kind: "myDay", count: openWork.filter((t) => belongsToScope(t, "myday", today)).length },
      { kind: "planned", count: openWork.filter((t) => t.dueOn !== null).length },
      { kind: "templates", count: rows.filter((t) => belongsToScope(t, "templates", today)).length },
    ],
    inbox: bucket(prepared, "inbox"),
    later: bucket(prepared, "later"),
    countDots,
  };
}
